// Assignments, quizzes and the student's own submissions.
//
// The lists and the submissions come from separate endpoints, so `loadStudent`
// fetches both and folds them together — that is what tells a card it has been
// handed in and what it scored.

import { get, post } from './client';
import {
  toUiAssignment, toUiQuiz, toUiSubmission, mergeSubmissions, serverDateToDmy,
} from './adapters';

// The server reports "none yet" as a 4xx error rather than an empty list in
// several places, and its status is unreliable (see materials.js for why), so
// these are matched on the message and only inside the 4xx range.
const nothingYet = (re) => (status, payload) =>
  status >= 400 && status < 500
  && re.test(payload?.data?.message || payload?.message || '');

/**
 * Read raw, because this endpoint puts its aggregate OUTSIDE `data`:
 *   { status, results: { count, submitted, submittedLate, missed },
 *     data: { assignments: [...] } }
 * The default unwrap returns `data` and would throw the counts away — they're
 * already computed server-side, so the dashboard shouldn't have to recount.
 */
export async function listAssignments() {
  const payload = await get('/assignment/getAllAssignments', {
    raw: true,
    isEmpty: nothingYet(/no assignments|not found/i),
    emptyValue: { data: { assignments: [] }, results: null },
  });
  const rows = Array.isArray(payload?.data?.assignments) ? payload.data.assignments : [];
  return { items: rows.map(toUiAssignment), totals: payload?.results || null };
}

export async function listQuizzes() {
  const data = await get('/quiz/getAllQuizzes', {
    isEmpty: nothingYet(/no quiz|not found/i),
    emptyValue: { quizzes: [] },
  });
  const rows = Array.isArray(data?.quizzes) ? data.quizzes : [];
  return rows.map(toUiQuiz);
}

// GET /student/showMySubmission — every submission this student has made.
export async function listMySubmissions() {
  const data = await get('/student/showMySubmission', {
    isEmpty: nothingYet(/no submission|not found/i),
    emptyValue: { submissions: [] },
  });
  const rows = Array.isArray(data?.submissions) ? data.submissions : [];
  return rows.map(toUiSubmission);
}

/**
 * Everything the student's Coursework page needs, in one call.
 * Requests run in parallel; submissions failing must not blank the lists, so it
 * degrades to "no submissions known" rather than rejecting outright.
 */
export async function loadStudentCoursework() {
  const [assignmentsResult, quizzes, submissions] = await Promise.all([
    listAssignments(),
    listQuizzes().catch(() => []),
    listMySubmissions().catch(() => []),
  ]);
  return {
    homework: mergeSubmissions(assignmentsResult.items, submissions, 'assignment'),
    quizzes: mergeSubmissions(quizzes, submissions, 'quiz'),
    submissions,
    totals: assignmentsResult.totals,
  };
}

/**
 * The student's own handed-in PDF. showMySubmission lists what was handed in but
 * carries no URLs, so this is fetched only when they ask to see one. The nesting
 * differs between the admin and student endpoints (`data.found` vs flat), so both
 * spellings are accepted and the caller just gets a URL.
 */
export async function getSubmissionFile(id) {
  const data = await get(`/student/showSubmission/${encodeURIComponent(id)}`);
  return (data?.found?.answers ?? data?.answers) || null;
}

/**
 * The MARKED copy, once an assistant has uploaded one.
 *   data = { markedPdf, assistant, score, markedAt }
 * Note `markedPdf`, not `marked` — the admin-side endpoint uses the other name.
 */
export async function getMarkedSubmission(id) {
  const data = await get(`/student/showMarkedSubmission/${encodeURIComponent(id)}`);
  if (!data) return null;
  const found = data?.found || data;
  return {
    file: found?.markedPdf || found?.marked || null,
    assistant: found?.assistant || '',
    score: found?.score ?? null,
    at: serverDateToDmy(found?.markedAt),
    /**
     * The assistant's written comment. It was being dropped here, so the one
     * endpoint that carries it had nowhere to surface it and no screen in the app
     * ever showed a student their feedback.
     *
     * The API normalises an empty comment to the STRING "N/A" (student_report's
     * `normalize`), which must not be printed as though the assistant had typed
     * it — so it becomes an empty string, the same as no comment at all.
     */
    feedback: !found?.feedback || found.feedback === 'N/A' ? '' : String(found.feedback),
  };
}

/**
 * Hands in a PDF. `answers` is the R2 URL from useR2Upload — the API stores the
 * URL, never the bytes.
 */
export const submitAssignment = (assignId, answersUrl) =>
  post(`/assignment/submitAssignment/${encodeURIComponent(assignId)}`, { answers: answersUrl });

export const submitQuiz = (quizId, answersUrl) =>
  post(`/quiz/submitQuiz/${encodeURIComponent(quizId)}`, { answers: answersUrl });
